import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUser } from '../../context/userContext.js';
import BaseLayout from '../../components/BaseLayout.js';
import Card from '../../components/Card.js';
import Grid from '@mui/material/Grid';
import { CookingPot } from '../../components/CookingSoon.js';
import { FeedbackTooltip } from '../../components/Tooltip.js';
import { analyzeFit, analyzeWording, getAIFeedbackResumeWording, getAIFeedbackResumeRewriting } from '../../api/axios.js';
import { SkillBubbles } from '../../components/Bubble.js';
import Typography from '@mui/material/Typography';
import { track } from '@vercel/analytics';
import {ReactComponent as ErrorIcon} from '../../static/icons/404 - Oli.svg';

const ResumeResults = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { formData, jobDescription } = location.state;
    const { refreshSubscription } = useUser();
    
    const [fitResults, setFitResults] = useState(null);
    const [overallWordingResults, setOverallWordingResults] = useState(null);
    const [experienceWordingResults, setExperienceWordingResults] = useState({});
    
    const [activeFeedbackSection, setActiveFeedbackSection] = useState(null);
    const [activeExperienceIndex, setActiveExperienceIndex] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    
    const toggleFeedback = (section) => {
        setActiveFeedbackSection(activeFeedbackSection === section ? null : section);
        // Reset experience index when changing sections
        if (section !== 'experiences') {
            setActiveExperienceIndex(null);
        }
    };
    
    const toggleExperienceFeedback = (index) => {
        if (activeExperienceIndex === index) {
            setActiveExperienceIndex(null);
            setActiveFeedbackSection(null);
        } else {
            setActiveExperienceIndex(index);
            setActiveFeedbackSection('experiences');
        }
    };

    useEffect(() => {
        const runAnalysis = async () => {
            if (formData) {
                try {
                    setIsLoading(true);
                    
                    const resumeText = `${formData.name} ${formData.email} ${formData.phone} ${formData.bio} ${formData.skills.join(' ')} ${formData.experiences.map(exp => `${exp.job_title} ${exp.company} ${exp.start_date} ${exp.end_date} ${exp.description}`).join(' ')} ${formData.education.join(' ')}`.trim();

                    // Fetch overall fit analysis
                    const fitResponse = await analyzeFit({
                        resume: resumeText,
                        jobDescription: jobDescription
                    });
                    setFitResults(fitResponse.data);
                    
                    // Fetch overall wording analysis
                    const wordingResponse = await analyzeWording({
                        text: resumeText
                    });
                    setOverallWordingResults(wordingResponse.data);
                    
                    // Fetch individual experience wording analysis
                    const experienceResults = {};
                    for (let i = 0; i < formData.experiences.length; i++) {
                        const experience = formData.experiences[i];
                        const expText = `${experience.job_title} ${experience.company} ${experience.description}`;
                        
                        // Gives scores
                        const expScore = await analyzeWording({
                            text: expText
                        });

                        let wordingFeedback = "";
                        let rewrittenSample = "";

                        console.log("Score: ", expScore.data.wordingScore);
                        if (expScore.data.wordingScore < 7)
                        {
                            try {
                                const wordingResponse = await getAIFeedbackResumeWording({
                                    resume: expText,
                                    JD: jobDescription
                                });
                                wordingFeedback = wordingResponse.data;
                                
                                const rewrittenResponse = await getAIFeedbackResumeRewriting({
                                    resume: expText,
                                    JD: jobDescription
                                });
                                rewrittenSample = rewrittenResponse.data;
                            } catch (error) {
                                console.error("Error fetching feedback:", error);
                            }
                        }

                        console.log("Customised feedback: ", wordingFeedback);
                        console.log("Rewritten sample: ", rewrittenSample);

                        experienceResults[i] = {
                            ...expScore.data,
                            wordingFeedback: wordingFeedback,
                            rewrittenSample: rewrittenSample
                        }
                    }
                    setExperienceWordingResults(experienceResults);
                    
                    setError(null);
                } catch (err) {
                    console.error('Error analyzing resume:', err);
                    if (err.response?.status === 400 && err.response?.data?.error === 'Contents failed guardrails check') {
                        setError('Your resume or job description contains inappropriate content. Please review and update your submission.');
                    } else {
                        setError('An unexpected error occurred while analyzing your resume. Please try again.');
                    }
                } finally {
                    track("Resume Analysis");
                    setIsLoading(false);
                }
            }
        };

        runAnalysis();
    }, [formData, jobDescription]);

    useEffect(() => {
        refreshSubscription();
    }, [location.pathname]);

    const renderScoreCard = () => {
        if (isLoading) return (
            <div className="loading-container">
                <CookingPot />
                <Typography variant="body2">Analysing your resume...</Typography>
            </div>
        );
        if ((!fitResults || !overallWordingResults) && !activeFeedbackSection) return null;

        const getScoreBand = (score) => {
            if (score < 4) return { level: 'Poor', color: 'text-red-500' };
            if (score < 7) return { level: 'Good', color: 'text-yellow-500' };
            return { level: 'Excellent', color: 'text-green-500' };
        };

        // For overall resume feedback
        const getOverallFeedback = () => {
            if (!overallWordingResults || !fitResults) return null;
            
            const wordingBand = getScoreBand(overallWordingResults.wordingScore);
            const relevanceBand = getScoreBand(fitResults.similarityScore * 10);

            const getWordingFeedback = (level) => {
                switch (level) {
                    case 'Poor':
                        return "Your resume's language needs significant improvement. Use action verbs, eliminate personal pronouns, and focus on quantifiable achievements.";
                    case 'Good':
                        return "While your writing is clear, you could make it more impactful. Try to include more specific metrics and achievements.";
                    case 'Excellent':
                        return "Your writing effectively communicates your professional achievements with strong action verbs and concrete metrics.";
                    default:
                        return "";
                }
            };
            
            const getRelevanceFeedback = (level) => {
                switch (level) {
                    case 'Poor':
                        return "Your resume needs more alignment with the job requirements. Review it carefully and customize your experience section.";
                    case 'Good':
                        return "Your resume shows some alignment with the position, but there's room for improvement.";
                    case 'Excellent':
                        return "Your resume is well-aligned with the position requirements.";
                    default:
                        return "";
                }
            };

            return (
                <div className="feedback-container">
                    <h3>Overall Resume Feedback</h3>
                    <div>
                        <div className="feedback-section">
                            <div className="header-row">
                                <h4>Wording</h4>
                                <span className={`score-band score-${wordingBand.level.toLowerCase()}`} data-cy="resumeResultsOverallWordingBand-span">
                                    {wordingBand.level}
                                </span>
                            </div>
                            <p>{getWordingFeedback(wordingBand.level)}</p>
                        </div>

                        <div className="feedback-section">
                            <div className="header-row">
                                <h4>Relevance</h4>
                                <span className={`score-band score-${relevanceBand.level.toLowerCase()}`} data-cy="resumeResultsOverallRelevanceBand-span">
                                    {relevanceBand.level}
                                </span>
                            </div>
                            <div>
                                <p>{getRelevanceFeedback(relevanceBand.level)}</p>
                            </div>
                        </div>
                    </div>
                </div>
            );
        };

        // For specific experience feedback
        const getExperienceFeedback = (index) => {
            if (!experienceWordingResults[index]) return null;
            
            const expWordingResult = experienceWordingResults[index];
            const wordingBand = getScoreBand(expWordingResult.wordingScore);
            
            return (
                <div className="feedback-container">
                    <h3>Experience Feedback</h3>
                    <div className="feedback-section">
                        <div className="header-row">
                            <h4>Wording Quality</h4>
                            <span className={`score-band score-${wordingBand.level.toLowerCase()}`} data-cy={`resumeResultsExp${index}WordingBand-span`}>
                                {wordingBand.level}
                            </span>
                        </div>
                        <p>{expWordingResult.wordingFeedback ? expWordingResult.wordingFeedback : getExperienceWordingAdvice(wordingBand.level, expWordingResult.isQuant)}</p>
                        <br/>
                        {expWordingResult.rewrittenSample && (
                            <div className="rewritten-sample-section">
                                <h4>Rewritten Sample</h4>
                                <p data-cy={`resumeResultsExp${index}Rewritten-p`}>{expWordingResult.rewrittenSample}</p>
                            </div>
                        )}
                    </div>
                </div>
            );
        };
        
        // Helper for experience feedback
        const getExperienceWordingAdvice = (level, hasQuantifiableImpact) => {
            let advice = "";
            
            switch (level) {
                case 'Poor':
                    advice = "Describe your role and how you made an impact.";
                    break;
                case 'Good':
                    advice = "This experience description is clear but could be more detailed.";
                    break;
                case 'Excellent':
                    advice = "This experience description effectively communicates your achievements.";
                    break;
                default:
                    advice = "";
            }
            
            if (!hasQuantifiableImpact) {
                advice += " Try to include quantifiable metrics or results to demonstrate your impact.";
            } else {
                advice += " Great job including metrics to demonstrate your impact!";
            }
            
            return advice;
        };

        // For skills feedback
        const getSkillsFeedback = () => {
            if (!fitResults) return null;
            
            const { missingKeywords } = fitResults;
            
            return (
                <div className="feedback-container">
                    {missingKeywords && missingKeywords.length > 0 ? (
                        <>
                        <div className="feedback-section">
                            <h4>Missing Skills</h4>
                            <p>Try including these either in your experiences or skills sections!</p>
                            <SkillBubbles skills={missingKeywords} />
                            <br/>
                            <h4>Skills you have:</h4>
                            <SkillBubbles skills={formData.skills} isMissing={false} />
                        </div> </> ) : (
                            <>
                        <div className="feedback-section">
                            <h4>Skills you have:</h4>
                            <br/>
                            <SkillBubbles skills={formData.skills} isMissing={false} />
                        </div> </>
                        )
                    }
                </div>
            );
        };

        // Determine which feedback to show based on active section/experience
        if (activeFeedbackSection === 'resume') {
            return getOverallFeedback();
        } else if (activeFeedbackSection === 'experiences' && activeExperienceIndex !== null) {
            return getExperienceFeedback(activeExperienceIndex);
        } else if (activeFeedbackSection === 'skills') {
            return getSkillsFeedback();
        }
        
        return null;
    };

    const handleError = async (e) => {
        e.preventDefault();

        navigate('/resume/upload');
    }

    return (
        <BaseLayout maxWidth='none'>
            <style>
                {`
                    .center-container {
                        display: flex;
                        padding-left: 2%;
                    }

                    .grid-container {
                        width: 100%;
                        margin: 0 auto;
                        padding: 24px;
                    }

                    .card-content {
                        max-height: calc(100vh - 200px);
                        overflow-y: auto;
                        overflow-x: hidden;
                        scrollbar-width: thin;
                        scrollbar-color: #6B3A8F rgba(26, 26, 26, 0.5);
                        padding: 20px;
                    }

                    .card-content::-webkit-scrollbar {
                        width: 6px;
                    }

                    .card-content::-webkit-scrollbar-track {
                        background: rgba(26, 26, 26, 0.5);
                    }

                    .card-content::-webkit-scrollbar-thumb {
                        background-color: #6B3A8F;
                        border-radius: 3px;
                    }

                    .resume-section {
                        margin-bottom: 24px;
                    }

                    h2 {
                        margin-bottom: 16px;
                        color: #E1E1E6;
                    }

                    h3 {
                        margin: 24px 0 16px 0;
                        color: #E1E1E6;
                    }

                    p {
                        margin-bottom: 8px;
                    }

                     strong {
                         color: #E1E1E6;
                         opacity: 0.9;
                     }

                     .section-header {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        width: 100%;
                        margin-bottom: 16px;
                     }
                     
                     .experience-header {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        width: 100%;
                        margin-top: 16px;
                        margin-bottom: 8px;
                     }

                     .loading-container {
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: center;
                        height: 100%;
                        padding: 24px;
                        text-align: center;
                    }

                     h4, h6 {
                         margin: 0;
                     }

                        .feedback-container {
                            display: flex;
                            flex-direction: column;
                            gap: 24px;
                        }

                        .feedback-section {
                            margin-bottom: 24px;
                        }

                        .header-row {
                            display: flex;
                            justify-content: space-between;
                            align-items: center;
                            margin-bottom: 8px;
                        }

                        .score-band {
                            font-family: 'Poppins', sans-serif;
                            font-size: 14px;
                        }

                        .score-poor {
                            color: #EF4444;
                        }

                        .score-good {
                            color: #FCD34D;
                        }

                        .score-excellent {
                            color: #22C55E;
                        }

                        p {
                            font-family: 'Poppins', sans-serif;
                            font-size: 14px;
                            color: #E1E1E6;
                            margin: 8px 0 0 0;
                            opacity: 0.8;
                        }

                        .redirect-button {
                            color: #E1E1E6;
                            font-family: 'Poppins', sans-serif;
                            font-size: 14px;
                            background: none;
                            border: none;
                            cursor: pointer;
                        }

                        .redirect-button:hover {
                            color: #9797FA;
                        }
                `}
            </style>
            <div className="center-container">
                <div className="grid-container">
                    {error && (
                        <div style={{width: '50%', margin: '0 auto'}}>
                        <Card>
                        <div className="card-content" style={{textAlign: 'center', padding: '40px 20px'}}>
                            <h2>Resume Analysis Error</h2>
                            <div style={{display: 'flex', justifyContent: 'center', margin: '24px 0'}}>
                                <ErrorIcon width='60' height='60'/>
                            </div>
                            <p style={{fontSize: '16px', marginBottom: '24px'}}>{error}</p>
                            <div style={{marginTop: '24px'}}>
                                <button 
                                    onClick={handleError}
                                    className='redirect-button'
                                >
                                    Try again with different content?
                                </button>
                            </div>
                        </div>
                    </Card>   
                    </div>        
                    )}
                    {!error && (
                        <Grid container spacing={4}>
                            <Grid item xs={12} md={8}>
                                <Card>
                                    <div className="card-content">
                                        <div className="resume-section">
                                            <div className="section-header">
                                                <h2>Resume</h2>
                                                <FeedbackTooltip 
                                                    onClick={() => toggleFeedback('resume')}
                                                    isActive={activeFeedbackSection === 'resume'}
                                                    identifier={`resumeResultsOverall-tooltip`}
                                                />
                                            </div>
                                            <br/>
                                            <h4>Profile</h4>
                                            <p><strong>Name:</strong> {formData.name}</p>
                                            <p><strong>Email:</strong> {formData.email}</p>
                                            <p><strong>Phone:</strong> {formData.phone}</p>
                                            <p><strong>Bio:</strong> {formData.bio}</p>
                                            <br/>
                                            <br/>
                                            <div className="section-header">
                                                <h4>Experiences</h4>
                                            </div>
                                            {formData.experiences.map((exp, index) => (
                                                <div key={index}>
                                                    <div className="experience-header">
                                                        <h6>Experience {index+1}</h6>
                                                        <FeedbackTooltip 
                                                            onClick={() => toggleExperienceFeedback(index)}
                                                            isActive={activeExperienceIndex === index && activeFeedbackSection === 'experiences'}
                                                            identifier={`resumeResultsExp[${index}]-tooltip`}
                                                        />
                                                    </div>
                                                    <p><strong>Job Title:</strong> {exp.job_title}</p>
                                                    <p><strong>Company:</strong> {exp.company}</p>
                                                    <p><strong>Duration:</strong> {exp.start_date} - {exp.end_date}</p>
                                                    <p><strong>Description:</strong> {exp.description}</p>
                                                </div>
                                            ))}
                                            <br/>
                                            <br/>
                                            <div className="section-header">
                                                <h4>Skills</h4>
                                                <FeedbackTooltip 
                                                    onClick={() => toggleFeedback('skills')}
                                                    isActive={activeFeedbackSection === 'skills'}
                                                />
                                            </div>
                                            <p>{formData.skills.join(', ')}</p>
                                        </div>

                                        <div className="resume-section">
                                            <h2>Job Description</h2>
                                            <p>{jobDescription}</p>
                                        </div>
                                    </div>
                                </Card>
                            </Grid>
                            {(activeFeedbackSection || activeExperienceIndex !== null) && (
                                <Grid item xs={12} md={4}>
                                    <Card>
                                        <div className="card-content">
                                            {renderScoreCard()}
                                        </div>
                                    </Card>
                                </Grid>   
                            )}
                        </Grid>
                    )}
                </div>
            </div>
        </BaseLayout>
    );
};

export default ResumeResults;